import { SearchOutlined } from "@ant-design/icons";
import { Alert, Input, Skeleton, Typography } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { FrequentReports } from "../components/hub/FrequentReports";
import { MyExportsPanel } from "../components/hub/MyExportsPanel";
import { OverviewStrip } from "../components/hub/OverviewStrip";
import { PeriodBar } from "../components/hub/PeriodBar";
import { ReportGroups } from "../components/hub/ReportGroups";
import { TelegramSummaryCard } from "../components/hub/TelegramSummaryCard";
import { usePeriodPreset } from "../hooks/usePeriodPreset";
import { useReportDescriptors } from "../hooks/useReports";
import { presetRange } from "../utils/period";

const { Title, Text } = Typography;

/**
 * Hisobotlar markazi (canvas «1 — Hisobotlar markazi», reja D1):
 * sarlavha + qidiruv → davr paneli → «bir qarashda» qatori → guruhlar;
 * o'ng ustunda eksportlarim, Telegram xulosasi va tez-tez ochilganlar.
 *
 * ⚠️ Eksport nomi (`ReportsPage`) MUZLATILGAN — `routeTree.tsx` shu
 * nom bilan import qiladi (kontrakt §0).
 *
 * ⚠️ Kartalar SERVERDAN keladigan descriptor ro'yxatidan quriladi —
 * guruhlash `REPORT_GROUPS` da, lekin ro'yxatda yo'q kind «Boshqa»
 * guruhida baribir chiziladi (spec §4.1).
 *
 * ⚠️ O'ng ustun kartalari o'z ruxsati ostida (`PermissionGate`) va
 * so'rovlari SHU kartalar ichida — faqat `reports:read` bo'lgan rolda
 * `/reports/exports` yoki `/settings` ga jim 403 ketmaydi.
 */
export function ReportsPage() {
	const { t } = useTranslation("reports");
	const { data: descriptorlar, isLoading, error } = useReportDescriptors();
	const { preset, setPreset } = usePeriodPreset();
	const [qidiruv, setQidiruv] = useState("");

	// ⚠️ Bugun BIR MARTA — yarim tunda so'rov kalitlari o'z-o'zidan
	// almashib, kartalar qayta yuklanmasin (`useReportFilters` naqshi).
	const bugun = useMemo(() => dayjs().format("YYYY-MM-DD"), []);
	const oraliq = useMemo(() => presetRange(preset, bugun), [preset, bugun]);
	const royxat = descriptorlar ?? [];

	return (
		<div className="flex flex-col gap-6 xl:flex-row xl:items-start">
			<div className="flex min-w-0 flex-1 flex-col gap-5">
				<div className="flex flex-wrap items-end justify-between gap-4">
					<div>
						<Title level={3} className="!mb-1">
							{t("list.title")}
						</Title>
						<Text type="secondary">
							{descriptorlar
								? t("hub.subtitle", { count: descriptorlar.length })
								: t("list.subtitle")}
						</Text>
					</div>
					<Input
						allowClear
						type="search"
						className="w-full sm:!w-[300px]"
						prefix={<SearchOutlined aria-hidden="true" />}
						placeholder={t("hub.searchPlaceholder")}
						aria-label={t("hub.searchPlaceholder")}
						value={qidiruv}
						onChange={(event) => setQidiruv(event.target.value)}
					/>
				</div>

				<PeriodBar preset={preset} range={oraliq} onChange={setPreset} />

				{error ? (
					<Alert type="error" showIcon message={getErrorMessage(error, t("list.loadError"))} />
				) : null}

				{isLoading ? <Skeleton active /> : null}

				{descriptorlar ? (
					<>
						{qidiruv.trim() ? null : (
							<OverviewStrip descriptors={royxat} range={oraliq} today={bugun} />
						)}
						<ReportGroups descriptors={royxat} query={qidiruv} range={oraliq} />
					</>
				) : null}
			</div>

			<aside className="flex w-full flex-none flex-col gap-4 xl:w-[320px] xl:pt-[58px]">
				<PermissionGate resource="reports" action="export">
					<MyExportsPanel descriptors={royxat} />
				</PermissionGate>
				<PermissionGate resource="settings" action="read">
					<TelegramSummaryCard descriptors={royxat} />
				</PermissionGate>
				<FrequentReports descriptors={royxat} range={oraliq} />
			</aside>
		</div>
	);
}
